// apps/admin/src/components/shell/AdminLanguageSelector.tsx
//
// Phase 8.x Debug — admin-local LanguageSelector wired to the real i18next
// instance shipped by apps/admin/src/i18n/config.ts.
//
// Before this component the AdminTopBar mounted `<LanguageSelector />` from
// `@printway/ui`, which is a Phase 1 visual-only placeholder (no `current`
// / `onChange` wiring, hard-coded EN/VI/ZH that does not match the admin's
// real vi/en/fr locale set — see packages/ui/src/components/TopBarRightSlot.tsx).
//
// Behaviour mirrors the vendor portal pattern (apps/vendor/src/components/
// LanguageSelector.tsx, Phase 7 Wave 18 Task 79 Amendment #1):
//   - Sources the live locale from `useTranslation('admin')` so the chip
//     re-renders on `languageChanged`.
//   - Flips the UI immediately via `changeAdminLocale()`, which persists
//     the choice to `localStorage['admin.locale']`.
//   - Best-effort PATCH `/admin/profile/preferences` so the choice survives
//     across devices / sessions. Pre-auth surfaces (login/forgot-password)
//     skip the PATCH — there is no authenticated user yet, and localStorage
//     keeps the pick across reload.
//   - Exclusive-dropdown coordination: shares the `topbar:dropdown:open`
//     event with sibling dropdowns so opening this menu collapses
//     NotificationBell / AccountDropdown / HelpDropdown.
//
// vg-binding: debug:B1-i18n

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
} from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { changeAdminLocale, type SupportedLocale } from '../../i18n/config.js';
import { apiClient } from '../../lib/apiClient.js';
import { useAuth } from '@printway/ui';

interface LanguageEntry {
  code: SupportedLocale;
  flag: string;
  label: string;
}

const LANGUAGES: ReadonlyArray<LanguageEntry> = [
  { code: 'vi', flag: '🇻🇳', label: 'Tiếng Việt' },
  { code: 'en', flag: '🇺🇸', label: 'English' },
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
] as const;

/**
 * Persist the picked locale to the admin profile so it survives reload and
 * cross-device sync. Best-effort: a network blip must not revert the chip,
 * which has already flipped via `changeAdminLocale`.
 */
async function persistLocale(next: SupportedLocale): Promise<void> {
  try {
    await apiClient.patch('/admin/profile/preferences', { language: next });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[AdminLanguageSelector] persistLocale failed', err);
  }
}

const TOPBAR_DROPDOWN_EVENT = 'topbar:dropdown:open';
let dropdownIdSeq = 0;

function useExclusiveDropdown(): [boolean, () => void, () => void] {
  const [open, setOpen] = useState(false);
  const idRef = useRef<string>('');
  if (idRef.current === '') {
    dropdownIdSeq += 1;
    idRef.current = `admin-lang-${dropdownIdSeq}`;
  }
  useEffect(() => {
    function onOpen(ev: Event): void {
      const detail = (ev as CustomEvent<{ id: string }>).detail;
      if (detail && detail.id !== idRef.current) setOpen(false);
    }
    function onDocClick(ev: MouseEvent): void {
      if (!open) return;
      const target = ev.target as HTMLElement | null;
      if (target?.closest('[data-admin-lang-root="true"]')) return;
      setOpen(false);
    }
    window.addEventListener(TOPBAR_DROPDOWN_EVENT, onOpen);
    // dbg-20260911-093626 — Escape and focus-out, to match the shared topbar
    // dropdowns. This copy of the hook had outside-click but neither of the
    // other two, so the menu could be dismissed with the mouse and not with
    // the keyboard.
    function onKeyDown(ev: KeyboardEvent): void {
      if (open && ev.key === 'Escape') setOpen(false);
    }
    function onFocusIn(ev: FocusEvent): void {
      if (!open) return;
      const target = ev.target as HTMLElement | null;
      if (target?.closest('[data-admin-lang-root="true"]')) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      window.removeEventListener(TOPBAR_DROPDOWN_EVENT, onOpen);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [open]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        window.dispatchEvent(
          new CustomEvent(TOPBAR_DROPDOWN_EVENT, { detail: { id: idRef.current } }),
        );
      }
      return next;
    });
  }, []);
  const close = useCallback(() => setOpen(false), []);
  return [open, toggle, close];
}

export function AdminLanguageSelector(): JSX.Element {
  const { t, locale } = useTranslation('admin');
  const user = useAuth((s) => s.user);
  const [open, toggle, close] = useExclusiveDropdown();

  const FALLBACK_LANG: LanguageEntry = { code: 'vi', flag: '🇻🇳', label: 'Tiếng Việt' };
  const current: LanguageEntry =
    LANGUAGES.find((l) => l.code === locale) ?? LANGUAGES[0] ?? FALLBACK_LANG;

  const handleSelect = (next: SupportedLocale): void => {
    void changeAdminLocale(next);
    close();
    if (!user) return; // pre-auth (login/forgot-password): skip PATCH, 401-noise
    void persistLocale(next);
  };

  return (
    <div className="relative" data-admin-lang-root="true">
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('chrome.languageSelector.selectLanguage')}
        data-testid="topbar-language-selector"
        className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-pw-text-muted transition-colors hover:bg-pw-surface hover:text-pw-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pw-primary-strong"
      >
        <LanguagesIcon className="h-4 w-4" aria-hidden />
        <span className="hidden md:inline">{current.code.toUpperCase()}</span>
        <Caret />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-44 overflow-hidden rounded-lg border border-pw-border bg-pw-bg shadow-lg"
        >
          {LANGUAGES.map((lang) => {
            const active = lang.code === current.code;
            return (
              <button
                key={lang.code}
                type="button"
                role="menuitem"
                data-testid={`topbar-language-option-${lang.code}`}
                onClick={() => handleSelect(lang.code)}
                className={
                  active
                    ? 'flex w-full items-center justify-between gap-2 bg-pw-surface px-3 py-2 text-left text-sm text-pw-text'
                    : 'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-pw-text-muted transition-colors hover:bg-pw-surface hover:text-pw-text'
                }
              >
                <span className="inline-flex items-center gap-2">
                  <span className="text-base leading-none" aria-hidden>
                    {lang.flag}
                  </span>
                  <span>{lang.label}</span>
                </span>
                {active ? <CheckIcon className="h-3.5 w-3.5 text-pw-primary-strong" aria-hidden /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default AdminLanguageSelector;

// ---------------------------------------------------------------------------
// Inline icons — keep this file self-contained.
// ---------------------------------------------------------------------------

function LanguagesIcon(props: { className?: string; 'aria-hidden'?: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={props.className}
      aria-hidden={props['aria-hidden']}
    >
      <path d="m5 8 6 6" />
      <path d="m4 14 6-6 2-3" />
      <path d="M2 5h12" />
      <path d="M7 2h1" />
      <path d="m22 22-5-10-5 10" />
      <path d="M14 18h6" />
    </svg>
  );
}

function CheckIcon(props: { className?: string; 'aria-hidden'?: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={props.className}
      aria-hidden={props['aria-hidden']}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function Caret(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3 w-3"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
